import { Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import { Lobby } from "./components/Lobby";
import { Game } from "./components/Game";
import { Maintenance, CheckMaintenance } from "./components/Maintenance";
import { useSoundStore } from "utils/sound-store";
import { useEffect, useRef } from "react";
import { Layout } from "components/Layout";
import { useOnlineStatus } from "hooks/useOnlineStatus";
import { NoInternet } from "components/NoInternet";
import { useStore } from "utils/store";
import { I18nProvider } from "./components/I18nProvider";
import { LanguageCode, LocalizationStyle } from "./types/styles";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import axios from "axios";

function App() {
  const [params] = useSearchParams();
  const style = params.get("style") || "default";
  const lang = params.get("lang") || "en";
  const gameId = params.get("gameId");
  const sessionId = params.get("sessionId");
  const returnUrl = params.get("returnUrl");
  const initialize = useSoundStore((store) => store.initialize);
  const isOnline = useOnlineStatus();
  const wasOfflineRef = useRef(false);
  const reset = useStore((state) => state.reset);
  const setReturnUrl = useStore((state) => state.setReturnUrl);

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchInterval: 30000,
        refetchOnWindowFocus: true,
        gcTime: 0,
        staleTime: 0,
        retry(failureCount, error: any) {
          let statusCode = error?.statusCode;

          if (axios.isAxiosError(error)) {
            statusCode = error.response?.status;
          }

          if (statusCode && statusCode > 400 && statusCode < 500) {
            return false;
          }

          return failureCount < 3;
        },
      },
    },
  });

  useEffect(() => {
    initialize();
  }, []);

  useEffect(() => {
    if (returnUrl) {
      setReturnUrl(returnUrl);
    }
  }, [returnUrl, setReturnUrl]);

  useEffect(() => {
    if (!isOnline) {
      wasOfflineRef.current = true;
      if (window.location.pathname.includes("/lobby")) reset();
    } else if (wasOfflineRef.current) {
      wasOfflineRef.current = false;
      if (window.location.pathname.includes("/game")) window.location.reload();
    }
  }, [isOnline, reset]);

  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider
        locale={(lang as string).toLowerCase() as LanguageCode}
        style={(style as string).toLowerCase() as LocalizationStyle}
      >
        <CheckMaintenance />
        {isOnline ? (
          <Routes>
            <Route path="/" element={<Layout />}>
              <Route path="maintenance" element={<Maintenance />} />
              <Route path="lobby" element={<Lobby />} />
              <Route path="game" element={<Game />} />
              <Route
                index
                element={
                  <Navigate
                    replace
                    to="lobby"
                    state={{
                      gameId,
                      sessionId,
                      returnUrl,
                    }}
                  />
                }
              />
            </Route>
          </Routes>
        ) : (
          <NoInternet />
        )}
      </I18nProvider>
    </QueryClientProvider>
  );
}

export default App;
